/**
 * Inhalte der DaVinciLab-Seite.
 *
 * Redaktioneller Text, keine Oberflächenbeschriftung — deshalb hier und nicht
 * in `ger.js`/`en.js`, nach demselben Muster wie die
 * Unterstützer:innen-Seite (`pages/SupporterRegister/content.js`).
 *
 * **Wofür die Seite da ist:** Sie soll einer Junior-Achievement-Organisation in
 * einem anderen Land zeigen, was hier läuft — und den Wunsch auslösen, das auch
 * zu haben. Deshalb steht sie unter einer eigenen Adresse und nicht in einem
 * Fenster: Was begeistern soll, muss man verschicken können.
 *
 * **Jede Zahl stammt aus derselben Auswertung wie die beiden Berichte**
 * (Schuljahr 2025/26, Auswertungsstand 24.08.2026, exkl. Junior Mini
 * Companies). Wer sie hier ändert, ändert sie dort mit — sonst widersprechen
 * sich zwei Seiten derselben Anwendung.
 *
 * ⚠️ **Zwei Zahlen gibt es bewusst nicht.** „Zugestellte Nachrichten" ist nicht
 * auswertbar (keine Aggregat-Route über Notifications; eine unternehmensweite
 * Nachricht erreicht jeweils das ganze Team) — genannt werden deshalb die
 * **Anlässe**, aus denen Nachrichten entstanden sind. Und es gibt keine Zahl
 * für ausgelöste Erinnerungen: Das einzige Feld der Schnittstelle dazu
 * (`remindersCount`) ist ein COUNT über mehrere Joins ohne DISTINCT und liefert
 * das Zeilenprodukt. Beides ist im Bericht „Die Plattform hinter Junior" in der
 * Methodik festgehalten.
 */

export const DAVINCILAB_PAGE = {
	ger: {
		/* -------------------------------------------------- Aufmacher */
		eyebrow: 'Junior Datenbank · Umsetzung und Betrieb: DaVinciLab',
		title: 'Zeit für Jugendliche.\nZeit für Lehrkräfte.\nNicht für Verwaltung.',
		lead: 'Im Schuljahr 2025/26: 592 Junior Companies, 4.966 Schüler:innen, 377 Lehrkräfte an 185 Schulen in 9 Bundesländern — begleitet von einer Administration, die klein bleibt. Die Lehrkräfte unterrichten, statt zu verwalten, und die Administration hat Zeit für die Lehrkräfte: Den Papierkram übernimmt die Plattform.',
		heroImage: 'mentorin',
		heroCaption: 'Wirtschaftsexpert:innen begleiten die Teams durchs Jahr.',

		/* -------------------------------------------------- Kennzahlen */
		figuresClaim: 'Ein Schuljahr, das sich weitgehend selbst verwaltet.',
		figuresPeriod: 'Schuljahr 2025/26',
		figures: [
			{ value: 592, label: 'Junior Companies', note: 'JC 446 · JCC 84 · JBC 62' },
			{ value: 4966, label: 'Schüler:innen', note: 'Ø 8,5 je Team, 557 in der Geschäftsführung' },
			{ value: 377, label: 'Lehrkräfte', note: '305 davon als betreuende Lehrkraft' },
			{ value: 185, label: 'Schulen', note: 'in 9 Bundesländern' },
			{ value: 12385, label: 'Dokumente erzeugt', note: 'kein einziges von Hand' },
			{ value: 9839, label: 'Anlässe für Nachrichten', note: 'jedes davon regelbasiert' }
		],
		figuresFoot:
			'Alle Zahlen dieser Seite stammen aus dem Schuljahr 2025/26 (Auswertungsstand 24.08.2026, ohne Junior Mini Companies) — dieselbe Auswertung wie in den beiden Berichten.',

		/* -------------------------------------------------- Checkliste */
		checklistClaim: 'Niemand muss fragen, was als Nächstes dran ist.',
		checklistTitle: 'Die Checkliste, die jede Junior Company für sich selbst führt',
		checklistText:
			'Jedes Team sieht seinen eigenen Stand: was erledigt ist, was noch fehlt, was der nächste Schritt voraussetzt — und den Weg dorthin gleich mit. Kein Merkblatt, keine Sammel-E-Mail, keine Rückfrage bei der Administration. Zehn Voraussetzungen, jede einzeln zuschaltbar, jede mit ihrem eigenen Verweis.',
		checklistItems: [
			'Unternehmensprofil und Risikoanalyse vollständig',
			'Mindestens zwei Schüler:innen im Team',
			'Mindestens ein:e Wirtschaftsexpert:in verbunden',
			'Einverständniserklärungen der Eltern geprüft',
			'Keine offenen Einladungen mehr',
			'Arbeitsbereich zugeordnet',
			'Firmen-E-Mail bestätigt',
			'Lebensmittelhygiene bestätigt — nur in sensiblen Branchen',
			'Skills4Retail-Konzept bewertet',
			'Kursnachweis hochgeladen'
		],
		checklistFoot:
			'Was davon gilt, entscheidet die Administration je Unternehmensform. Und was die Oberfläche verlangt, verlangt auch die Schnittstelle — eine Checkliste, die sich umgehen lässt, ist keine.',
		checklistImage: 'idee',

		/* -------------------------------------------------- Nachrichten */
		messagesClaim: 'Die Plattform meldet sich. Nicht die Verwaltung.',
		messagesTitle: 'Nachrichten im richtigen Moment, an die richtige Rolle',
		messagesText:
			'Jede Nachricht hängt an einem Auslöser: einem Ereignis oder einer Frist. Keine wird von Hand angestoßen, keine geht an alle. Wer sie bekommt, entscheiden Rolle, Bundesland, Schultyp, Unternehmensform, Branche und betreuende Lehrkraft — 13 Läufe prüfen jede Nacht um 03:00 Uhr die Fristen, einer wöchentlich, und jede Erinnerung geht genau einmal hinaus.',
		messagesStats: [
			{ value: '87', label: 'Vorlagen', note: '45 als E-Mail, 42 in der Anwendung' },
			{ value: '77 / 10', label: 'Auslöser', note: 'ereignis- / fristgesteuert' },
			{ value: '14', label: 'Regelläufe', note: '13 nächtlich, 1 wöchentlich' }
		],
		phasesTitle: 'Über den ganzen Lebenszyklus verteilt',
		phases: [
			{ label: 'Anmeldung', value: 22 },
			{ label: 'Team & Betreuung', value: 15 },
			{ label: 'Aktivierung', value: 20 },
			{ label: 'Laufender Betrieb', value: 13 },
			{ label: 'Bericht & Abrechnung', value: 12 },
			{ label: 'Abschluss', value: 5 }
		],
		audiencesTitle: 'An wen sie sich richten',
		audiences: [
			{ label: 'Schüler:innen', value: 58 },
			{ label: 'Lehrkräfte', value: 27 },
			{ label: 'Unterstützer:innen', value: 18 },
			{ label: 'Administration', value: 2 }
		],
		occasionsTitle: 'Woraus die 9.839 Anlässe dieses Schuljahrs entstanden sind',
		occasions: [
			{ label: 'Beitritte ins Team', value: 4966 },
			{ label: 'Rechnungen erstellt', value: 828 },
			{ label: 'Berichte eingereicht', value: 827 },
			{ label: 'Zahlungen verbucht', value: 688 },
			{ label: 'Unternehmen angelegt', value: 592 },
			{ label: 'Lehrkraft zugeordnet', value: 581 },
			{ label: 'Firmen-E-Mail bestätigt', value: 538 },
			{ label: 'Freigaben', value: 527 },
			{ label: 'Abschlüsse mit Urkunde', value: 281 },
			{ label: 'Ablehnungen', value: 11 }
		],
		occasionsFoot:
			'„Anlässe" sind die Ereignisse, an denen Nachrichten hängen — nicht die Zahl zugestellter Nachrichten. Die lässt sich nicht messen: Eine unternehmensweite Nachricht erreicht jeweils das ganze Team, im Schnitt 8,5 Personen.',
		messagesImage: 'plattform',

		/* -------------------------------------------------- Orientierung */
		orientationClaim: 'Ein Blick genügt.',
		orientationTitle: 'Wo alle stehen, ohne dass jemand nachfragt',
		orientationText:
			'Fortschritt ist hier keine Statusspalte, sondern ein Bild. Wer eine Junior Company öffnet, sieht in einer Sekunde, wie weit sie ist — und wer die Verwaltung öffnet, sieht dasselbe für alle auf einmal.',
		orientationItems: [
			{
				title: 'Fortschrittsbalken je Unternehmen',
				text: 'Vier Abschnitte — Anmeldung, Genehmigung, Geschäftstätigkeit, Berichte — mit rund 16 Schritten. Dieselbe Darstellung bei Schüler:innen, Lehrkraft und Verwaltung.'
			},
			{
				title: 'Ampeln am Profil',
				text: 'Jeder Abschnitt des Unternehmensprofils trägt seinen eigenen Stand. Wer etwas beanstandet, nennt in jeder Rolle denselben Namen.'
			},
			{
				title: 'Trichter über den Jahrgang',
				text: 'Neun Stufen von der Anmeldung bis zur Urkunde, gestapelt nach Unternehmensform. Jede Stufe ist anklickbar und filtert die Liste darunter.'
			},
			{
				title: 'Dashboard in drei Spalten',
				text: 'Nächste Schritte, Mitteilungen, Neuigkeiten — für Schüler:innen und Lehrkräfte, jeweils aus ihrer eigenen Sicht.'
			}
		],

		/* -------------------------------------------------- Dokumente */
		documentsClaim: '12.385 Dokumente. Kein einziges von Hand.',
		documentsTitle: 'Was am Ende in der Mappe liegt',
		documentsText:
			'Jedes Dokument entsteht aus denselben Daten, die in der Anwendung stehen — es gibt keinen zweiten Datenbestand, der veralten könnte, und keine Vorlage, die jemand vor dem Versand ausfüllt.',
		documents: [
			{ value: 6651, label: 'Anteilsscheine', note: 'für die Anteilseigner:innen jeder Junior Company' },
			{ value: 4327, label: 'Teilnahmeurkunden', note: 'auf den Namen jeder einzelnen Person' },
			{ value: 855, label: 'Rechnungen', note: 'samt Versicherungsbeitrag als eigener Position' },
			{ value: 552, label: 'Firmenzertifikate', note: 'für das Unternehmen als Ganzes' }
		],
		uploadsTitle: 'Dazu geprüft und abgelegt',
		uploads: [
			{ value: 4882, label: 'Einverständniserklärungen' },
			{ value: 303, label: 'Berichtsanlagen' },
			{ value: 157, label: 'Businesspläne' },
			{ value: 47, label: 'Kursnachweise' }
		],
		documentsImage: 'urkunde',

		/* -------------------------------------------------- Rollen */
		rolesClaim: 'Jede Rolle bekommt ihre eigene Anwendung.',
		rolesTitle: 'Fünf Sichten auf dieselben Daten',
		rolesText:
			'Sichtbarkeit ist hier keine Frage der Oberfläche, sondern der Abfrage: Eine Landesadministration bekommt fremde Bundesländer nicht als ausgeblendete Zeile, sondern gar nicht. Das ist der Unterschied zwischen „wird nicht angezeigt" und „ist nicht zugänglich".',
		roles: [
			{ title: 'Schüler:in', text: 'Gründet, führt, reicht Berichte ein, fragt Expert:innen an.' },
			{ title: 'Lehrkraft', text: 'Betreut, gibt frei, bewertet, lädt Nachweise hoch.' },
			{ title: 'Landesadministration', text: 'Prüft, verrechnet, stellt aus — im eigenen Bundesland.' },
			{ title: 'Bundesadministration', text: 'Führt Stammdaten, Vorlagen und Einstellungen.' },
			{ title: 'Unterstützer:in', text: 'Registriert sich selbst, nimmt Anfragen an oder ab.' }
		],

		/* -------------------------------------------------- Wirkung */
		effectClaim: 'Autopilot für die Administration. Zeit für die Menschen.',
		effectTitle: 'Was das im Alltag bedeutet',
		effectText:
			'Jeder der 9.839 Anlässe dieses Schuljahrs wäre sonst ein eigener Vorgang gewesen: nachschlagen, schreiben, versenden. Bei jedem der 12.385 Dokumente dasselbe. Niemand in der Administration hat sie geschrieben, und trotzdem sind sie zur richtigen Zeit bei den Richtigen angekommen. Der Punkt ist nicht, was das erspart, sondern wofür die Administration stattdessen da ist: für die Klasse, die nicht weiterweiß, und für die Junior Company, die kurz vor dem Aufgeben steht.',
		effectImage: 'pitch',

		/* -------------------------------------------------- Abschluss */
		outroClaim: 'Gebaut von Menschen, die Schulen kennen.',
		outroTitle: 'DaVinciLab',
		outroText:
			'Die DaVinciLab GmbH entwickelt und betreibt die Junior Datenbank im Auftrag von Junior Achievement Austria — Anforderung, Umsetzung, Prüfung, Auslieferung und Betrieb an einer Stelle, ohne Übergabe, an der etwas verloren geht. DaVinciLab arbeitet an digitaler Bildung; deshalb ist diese Anwendung so gebaut, dass eine 14-Jährige sie ohne Einschulung bedient.',
		outroLinks: [
			{
				title: 'Junior-Bilanz 2025/26',
				text: 'Was ein Schuljahr Junior bewegt — öffentliche Fassung, ohne finanzielle Ergebnisse.',
				to: 'bilanz'
			},
			{
				title: 'Die Plattform hinter Junior',
				text: 'Der nüchterne Beleg: Automatismen, Umfang, Prüfung, Lieferkadenz — mit Methodik.',
				to: 'plattform'
			}
		],
		contactTitle: 'Kontakt',
		contact: 'DaVinciLab GmbH · Poschgasse 3/51, 1140 Wien\noffice@davincilab.at · FN 574068v, Handelsgericht Wien',
		imageNote:
			'Alle Fotos dieser Seite sind KI-generiert und zeigen keine realen Personen — wie im Jahresbericht, aus dem sie stammen.'
	},

	en: {
		eyebrow: 'Junior database · Built and operated by DaVinciLab',
		title: 'Time for young people.\nTime for teachers.\nNot for paperwork.',
		lead: 'In the 2025/26 school year: 592 Junior Companies, 4,966 students, 377 teachers at 185 schools across 9 federal states — supported by an administration that stays small. The teachers teach instead of filing, and the administration has time for the teachers: the platform does the paperwork.',
		heroImage: 'mentorin',
		heroCaption: 'Business experts accompany the teams through the year.',

		figuresClaim: 'A school year that largely administers itself.',
		figuresPeriod: '2025/26 school year',
		figures: [
			{ value: 592, label: 'Junior Companies', note: 'JC 446 · JCC 84 · JBC 62' },
			{ value: 4966, label: 'students', note: '8.5 per team on average, 557 as managing directors' },
			{ value: 377, label: 'teachers', note: '305 of them as supervising teacher' },
			{ value: 185, label: 'schools', note: 'across 9 federal states' },
			{ value: 12385, label: 'documents generated', note: 'not one of them by hand' },
			{ value: 9839, label: 'events behind messages', note: 'every one of them rule-based' }
		],
		figuresFoot:
			'Every figure on this page comes from the 2025/26 school year (data as of 24 August 2026, excluding Junior Mini Companies) — the same analysis as in both reports.',

		checklistClaim: 'Nobody has to ask what comes next.',
		checklistTitle: 'The checklist every Junior Company keeps for itself',
		checklistText:
			'Every team sees its own state: what is done, what is missing, what the next step requires — and the way there. No handout, no bulk email, no query to the administration. Ten prerequisites, each one switchable, each with its own link.',
		checklistItems: [
			'Company profile and risk analysis complete',
			'At least two students on the team',
			'At least one business expert connected',
			'Parental consent forms checked',
			'No invitations left pending',
			'Work area assigned',
			'Company email confirmed',
			'Food hygiene confirmed — sensitive industries only',
			'Skills4Retail concept assessed',
			'Course certificate uploaded'
		],
		checklistFoot:
			'Which of these apply is decided per company type by the administration. And whatever the interface demands, the API demands too — a checklist you can walk around is not one.',
		checklistImage: 'idee',

		messagesClaim: 'The platform gets in touch. Not the administration.',
		messagesTitle: 'Messages at the right moment, to the right role',
		messagesText:
			'Every message has a trigger: an event or a deadline. None is sent by hand, none goes to everyone. Who receives it is decided by role, federal state, school type, company type, industry and supervising teacher — 13 jobs check the deadlines every night at 03:00, one weekly, and every reminder goes out exactly once.',
		messagesStats: [
			{ value: '87', label: 'templates', note: '45 as email, 42 in the application' },
			{ value: '77 / 10', label: 'triggers', note: 'by event / by deadline' },
			{ value: '14', label: 'scheduled runs', note: '13 nightly, 1 weekly' }
		],
		phasesTitle: 'Spread across the whole lifecycle',
		phases: [
			{ label: 'Registration', value: 22 },
			{ label: 'Team & supervision', value: 15 },
			{ label: 'Activation', value: 20 },
			{ label: 'Day-to-day', value: 13 },
			{ label: 'Reporting & billing', value: 12 },
			{ label: 'Closing', value: 5 }
		],
		audiencesTitle: 'Who they address',
		audiences: [
			{ label: 'Students', value: 58 },
			{ label: 'Teachers', value: 27 },
			{ label: 'Supporters', value: 18 },
			{ label: 'Administration', value: 2 }
		],
		occasionsTitle: 'What the 9,839 events of this school year were',
		occasions: [
			{ label: 'Team joins', value: 4966 },
			{ label: 'Invoices issued', value: 828 },
			{ label: 'Reports submitted', value: 827 },
			{ label: 'Payments booked', value: 688 },
			{ label: 'Companies created', value: 592 },
			{ label: 'Teacher assigned', value: 581 },
			{ label: 'Company email confirmed', value: 538 },
			{ label: 'Approvals', value: 527 },
			{ label: 'Completions with certificate', value: 281 },
			{ label: 'Rejections', value: 11 }
		],
		occasionsFoot:
			'“Events” are the occurrences messages hang on — not the number of messages delivered. That cannot be measured: a company-wide message reaches the whole team, 8.5 people on average.',
		messagesImage: 'plattform',

		orientationClaim: 'One glance is enough.',
		orientationTitle: 'Where everyone stands, without anyone asking',
		orientationText:
			'Progress here is not a status column, it is a picture. Open a Junior Company and you see in a second how far it has come — open the administration and you see the same for all of them at once.',
		orientationItems: [
			{
				title: 'Progress bar per company',
				text: 'Four sections — registration, approval, business activity, reports — with around 16 steps. The same view for students, teachers and administration.'
			},
			{
				title: 'Traffic lights on the profile',
				text: 'Every section of the company profile carries its own state. Whoever raises an issue names the same section in every role.'
			},
			{
				title: 'Funnel across the cohort',
				text: 'Nine stages from registration to certificate, stacked by company type. Every stage is clickable and filters the list below.'
			},
			{
				title: 'Dashboard in three columns',
				text: 'Next steps, notifications, news — for students and teachers, each from their own perspective.'
			}
		],

		documentsClaim: '12,385 documents. Not one by hand.',
		documentsTitle: 'What ends up in the folder',
		documentsText:
			'Every document is generated from the same data that sits in the application — there is no second set of records to go stale, and no template anyone fills in before sending.',
		documents: [
			{ value: 6651, label: 'share certificates', note: 'for the shareholders of every Junior Company' },
			{ value: 4327, label: 'participation certificates', note: 'in the name of each individual person' },
			{ value: 855, label: 'invoices', note: 'insurance fee as a separate line' },
			{ value: 552, label: 'company certificates', note: 'for the company as a whole' }
		],
		uploadsTitle: 'Checked and filed on top of that',
		uploads: [
			{ value: 4882, label: 'parental consent forms' },
			{ value: 303, label: 'report attachments' },
			{ value: 157, label: 'business plans' },
			{ value: 47, label: 'course certificates' }
		],
		documentsImage: 'urkunde',

		rolesClaim: 'Every role gets its own application.',
		rolesTitle: 'Five views of the same data',
		rolesText:
			'Visibility here is not a question of the interface but of the query: a state administration does not get other states as a hidden row, it does not get them at all. That is the difference between “not displayed” and “not accessible”.',
		roles: [
			{ title: 'Student', text: 'Founds, runs, submits reports, requests experts.' },
			{ title: 'Teacher', text: 'Supervises, approves, assesses, uploads evidence.' },
			{ title: 'State administration', text: 'Reviews, bills, issues — within its own state.' },
			{ title: 'Federal administration', text: 'Maintains master data, templates and settings.' },
			{ title: 'Supporter', text: 'Registers independently, accepts or declines requests.' }
		],

		effectClaim: 'Autopilot for the administration. Time for the people.',
		effectTitle: 'What this means day to day',
		effectText:
			'Each of the 9,839 events of this school year would otherwise have been a case of its own: look it up, write it, send it. The same for each of the 12,385 documents. Nobody in the administration wrote them, and still they reached the right people at the right time. The point is not what that saves but what the administration is there for instead: the class that is stuck, and the Junior Company about to give up.',
		effectImage: 'pitch',

		outroClaim: 'Built by people who know schools.',
		outroTitle: 'DaVinciLab',
		outroText:
			'DaVinciLab GmbH develops and operates the Junior database on behalf of Junior Achievement Austria — requirement, implementation, testing, release and operations in one place, with no handover where something gets lost. DaVinciLab works on digital education; that is why this application is built so a 14-year-old can use it without training.',
		outroLinks: [
			{
				title: 'Junior-Bilanz 2025/26',
				text: 'What a school year of Junior moves — public edition, without financial results.',
				to: 'bilanz'
			},
			{
				title: 'The platform behind Junior',
				text: 'The sober evidence: automation, scope, testing, release cadence — with methodology.',
				to: 'plattform'
			}
		],
		contactTitle: 'Contact',
		contact:
			'DaVinciLab GmbH · Poschgasse 3/51, 1140 Vienna\noffice@davincilab.at · FN 574068v, Commercial Court Vienna',
		imageNote:
			'All photos on this page are AI-generated and show no real people — as in the annual report they come from.'
	}
}

/** Liefert die Inhalte zur aktiven Sprache, mit Deutsch als Rückfall. */
export const getDaVinciLabPage = language => DAVINCILAB_PAGE[String(language).startsWith('en') ? 'en' : 'ger']
